// Catálogo Región → Comuna para los selectores de tarifa, con dos modos de operación
// según lo que el operador haya declarado en /admin/settings?tab=coverage.
//
//   'coverage' — hay cobertura declarada: los selectores se acotan a ella. El catálogo
//                sale del módulo `geo` del BFF (regiones.json / comunas_by_region.json),
//                que es el ÚNICO que expone los ObjectIds con los que está expresada
//                `logisticscompanies.coverageAreas`. Cuesta una request por región
//                cubierta, pero solo se barren las cubiertas.
//
//   'country'  — no hay cobertura declarada (`[]`) o no se pudo leer (`null`): se ofrece
//                el catálogo completo del país. Acá el filtro por id no aplica, así que
//                NO hacen falta ObjectIds y se usa `contracts/countries/regions`, que
//                devuelve las regiones con sus comunas anidadas en UNA sola request. Por
//                el stack geo esto costaría 16 llamadas en Chile y 24 en Argentina,
//                contra un rate limit de 200/60s en el BFF.
//
// Por qué existe el modo 'country' y no se bloquea el editor como hace ComunaZonesEditor:
// los campos que consumen este hook hoy son de TEXTO LIBRE, así que un operador que nunca
// declaró su cobertura puede tarifar cualquier comuna. Bloquearlo al migrar a selectores
// sería quitarle una capacidad que tiene, en una pantalla que factura.
//
// DUPLICACIÓN CONSCIENTE: ComunaZonesEditor (pago a repartidores) resuelve el modo
// 'coverage' con su propia copia de esta lógica y sus propios caches. No se migró a este
// hook a propósito: es la ruta que liquida el pago a repartidores, la cubren 20 tests que
// el CI no ejecuta, y el refactor no formaba parte del alcance pedido. Migrarlo es un
// paso posterior aislado, con esos tests como red.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRegions as getGeoRegions, getCommunesByRegion } from "../api/settings";
import { getRegions as getCountryRegions } from "../api/contracts";
import { useCountry } from "../context/CountryContext";

// Caches a nivel de MÓDULO (persisten entre montajes en la misma sesión). Guardan el
// catálogo CRUDO, sin filtrar por cobertura: el filtro se aplica al usarlo, así un cambio
// de cobertura se refleja sin invalidar el cache.
const geoRegionsByCountry = new Map(); // countryId -> [{ value: regionId, label }]
const geoCommunesByRegion = new Map(); // regionId  -> [{ value: name, label, id }]
// El catálogo de país se cachea como PROMESA para que dos filas montadas a la vez no
// disparen dos requests; el de geo usa un Set de in-flight por el mismo motivo.
const countryCatalogPromises = new Map(); // countryId -> Promise<{ regions, communesByRegion }>

/**
 * Normalización espejo de la del backend (NFD, sin diacríticos, minúsculas, trim).
 * U+0300–U+036F son las marcas diacríticas combinantes que deja NFD al descomponer.
 */
export const normalizeCommuneName = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/**
 * Las comunas se guardan en minúsculas; se muestran capitalizadas.
 *
 * Se capitaliza la letra que sigue a un separador REAL, no la que sigue a un `\b`: el
 * word boundary de JS se define sobre `[A-Za-z0-9_]`, así que en "concepción" ve un
 * límite antes de la `ó` y devolvería "ConcepciÓn" (y "viña del mar" -> "ViÑa Del Mar").
 * El resto de la palabra se deja intacto a propósito: un nombre ya guardado en mayúsculas
 * se muestra como está, en vez de reescribirlo.
 */
export const titleCaseCommune = (s) =>
  String(s || "").replace(
    /(^|[\s\-'’/])(\p{L})/gu,
    (_, separator, letter) => separator + letter.toUpperCase(),
  );

/**
 * `coverageAreas` -> Map(regionId -> Set(communeId)).
 * Convención del backend (contracts.service: "communeIds vacío significa región
 * completa"): un `Set` vacío habilita TODAS las comunas de esa región.
 */
const buildCoverageMap = (coverageAreas) => {
  const map = new Map();
  (Array.isArray(coverageAreas) ? coverageAreas : []).forEach((area) => {
    if (!area?.regionId) return;
    const ids = (Array.isArray(area.communeIds) ? area.communeIds : []).map(String);
    map.set(String(area.regionId), new Set(ids));
  });
  return map;
};

/**
 * Carga el catálogo completo del país en una request y lo indexa por CÓDIGO de región.
 *
 * Las claves de este modo son códigos de región ("13"), no ObjectIds: `contracts/
 * countries/*` declara sus subdocumentos con `@Schema({ _id: false })` y no los expone.
 * No importa, porque en modo 'country' no hay nada que cruzar contra la cobertura y el
 * `regionId` de la fila es estado efímero de UI que nunca se persiste (lo único que viaja
 * al backend son los nombres de comuna).
 */
const loadCountryCatalog = (countryId) => {
  if (!countryCatalogPromises.has(countryId)) {
    countryCatalogPromises.set(
      countryId,
      getCountryRegions({ countryId })
        .then((data) => {
          if (!Array.isArray(data)) throw new Error("catálogo de país no disponible");
          const regions = [];
          const communesByRegion = new Map();
          data.forEach((region) => {
            const key = String(region?.code ?? region?.name ?? "");
            if (!key) return;
            regions.push({ value: key, label: region.name });
            communesByRegion.set(
              key,
              (region?.communes ?? [])
                .filter((c) => c?.name)
                .map((c) => ({ value: c.name, label: c.name, id: null })),
            );
          });
          regions.sort((a, b) => a.label.localeCompare(b.label, "es"));
          return { regions, communesByRegion };
        })
        .catch((err) => {
          // Se limpia para que un fallo de red no deje el cache envenenado toda la sesión.
          countryCatalogPromises.delete(countryId);
          console.warn("useCommuneCatalog: no se pudo leer el catálogo del país", err);
          return null;
        }),
    );
  }
  return countryCatalogPromises.get(countryId);
};

/**
 * @param {{ coverageAreas: Array|null, coverageLoading?: boolean }} params
 *   `coverageAreas`: tal cual viene en `logisticscompanies.coverageAreas`.
 *     - Array no vacío = cobertura declarada -> modo 'coverage'.
 *     - `[]`           = no declaró cobertura -> modo 'country'.
 *     - `null`         = todavía no se sabe o falló la lectura -> modo 'country' también,
 *       pero con `coverageKnown: false` para que el consumidor explique cuál de los dos
 *       casos es. Nunca se afirma que una comuna esté "fuera de cobertura" cuando la
 *       cobertura no se pudo leer.
 */
export default function useCommuneCatalog({ coverageAreas, coverageLoading = false }) {
  const { countryId, countryName, loading: countryLoading } = useCountry();

  const coverageKnown = !coverageLoading && Array.isArray(coverageAreas);
  const coverage = useMemo(() => buildCoverageMap(coverageAreas), [coverageAreas]);

  const mode = coverageLoading
    ? "loading"
    : coverageKnown && coverage.size > 0
      ? "coverage"
      : "country";

  const [geoRegions, setGeoRegions] = useState(
    () => geoRegionsByCountry.get(countryId) ?? [],
  );
  const [geoCommunes, setGeoCommunes] = useState({});
  // Estado de carga por región: 'loading' | 'loaded' | 'error'. Explícito para no quedar
  // en "Cargando…" para siempre si una request falla.
  const [communeStatus, setCommuneStatus] = useState({});
  const [countryCatalog, setCountryCatalog] = useState(null);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const inFlight = useRef(new Set());

  // ── Modo 'coverage': regiones del módulo geo (una vez por país, cacheado) ──────────
  useEffect(() => {
    if (mode !== "coverage" || countryLoading || !countryId) return undefined;
    const cached = geoRegionsByCountry.get(countryId);
    if (cached) {
      setGeoRegions(cached);
      return undefined;
    }
    let alive = true;
    setLoadingRegions(true);
    getGeoRegions(countryId)
      .then((res) => {
        const data = res?.data ?? res;
        if (!alive || !Array.isArray(data)) return;
        const opts = data.map((r) => ({ value: String(r._id), label: r.name }));
        geoRegionsByCountry.set(countryId, opts);
        setGeoRegions(opts);
      })
      .catch(() => {})
      .finally(() => alive && setLoadingRegions(false));
    return () => {
      alive = false;
    };
  }, [mode, countryId, countryLoading]);

  // ── Modo 'country': catálogo completo en una request ───────────────────────────────
  useEffect(() => {
    if (mode !== "country" || countryLoading || !countryId) return undefined;
    let alive = true;
    setLoadingRegions(true);
    loadCountryCatalog(countryId)
      .then((cat) => {
        if (alive) setCountryCatalog(cat);
      })
      .finally(() => alive && setLoadingRegions(false));
    return () => {
      alive = false;
    };
  }, [mode, countryId, countryLoading]);

  /** Carga idempotente de las comunas de una región (solo modo 'coverage'). */
  const ensureCommunes = useCallback(
    async (regionId) => {
      if (mode !== "coverage" || !regionId) return;
      const cached = geoCommunesByRegion.get(regionId);
      if (cached) {
        setGeoCommunes((m) => (m[regionId] ? m : { ...m, [regionId]: cached }));
        setCommuneStatus((s) =>
          s[regionId] === "loaded" ? s : { ...s, [regionId]: "loaded" },
        );
        return;
      }
      if (inFlight.current.has(regionId)) return;
      inFlight.current.add(regionId);
      setCommuneStatus((s) => ({ ...s, [regionId]: "loading" }));
      try {
        const res = await getCommunesByRegion(regionId);
        const data = res?.data ?? res;
        const communes = data?.communes;
        if (Array.isArray(communes)) {
          const opts = communes.map((c) => ({
            value: c.name,
            label: c.name,
            id: String(c._id),
          }));
          geoCommunesByRegion.set(regionId, opts);
          setGeoCommunes((m) => ({ ...m, [regionId]: opts }));
          setCommuneStatus((s) => ({ ...s, [regionId]: "loaded" }));
        } else {
          setCommuneStatus((s) => ({ ...s, [regionId]: "error" }));
        }
      } catch {
        setCommuneStatus((s) => ({ ...s, [regionId]: "error" }));
      } finally {
        inFlight.current.delete(regionId);
      }
    },
    [mode],
  );

  // Regiones ofrecidas: las cubiertas (modo 'coverage') o todas las del país.
  const regionOptions = useMemo(() => {
    if (mode === "loading") return [];
    if (mode === "coverage") return geoRegions.filter((r) => coverage.has(r.value));
    return countryCatalog?.regions ?? [];
  }, [mode, geoRegions, coverage, countryCatalog]);

  // Comunas seleccionables de una región: las declaradas en la cobertura, o todas si la
  // región está cubierta completa (`communeIds: []`) o si el modo es 'country'.
  const communeOptions = useCallback(
    (regionId) => {
      if (!regionId) return [];
      if (mode === "country") return countryCatalog?.communesByRegion?.get(regionId) ?? [];
      if (mode !== "coverage") return [];
      const all = geoCommunes[regionId] ?? [];
      const allowed = coverage.get(regionId);
      if (!allowed || allowed.size === 0) return all;
      return all.filter((c) => allowed.has(c.id));
    },
    [mode, geoCommunes, coverage, countryCatalog],
  );

  // Barrido eager de las regiones OFRECIDAS en modo 'coverage'. No es para poblar los
  // dropdowns (eso es perezoso, al elegir región): es lo que permite saber qué nombres
  // ya guardados dejaron de ser seleccionables y marcarlos en pantalla.
  useEffect(() => {
    if (mode !== "coverage" || regionOptions.length === 0) return undefined;
    let alive = true;
    (async () => {
      await Promise.all(regionOptions.map((r) => ensureCommunes(r.value)));
      if (!alive) return;
    })();
    return () => {
      alive = false;
    };
  }, [mode, regionOptions, ensureCommunes]);

  // Nombres (normalizados) que el operador PUEDE elegir hoy.
  const selectableNames = useMemo(() => {
    const set = new Set();
    regionOptions.forEach((r) => {
      communeOptions(r.value).forEach((c) => set.add(normalizeCommuneName(c.value)));
    });
    return set;
  }, [regionOptions, communeOptions]);

  // ¿Se puede AFIRMAR que un nombre no es seleccionable? Solo con el catálogo de todas
  // las regiones ofrecidas cargado. Con una carga a medias marcaríamos como huérfanas
  // comunas que sí están, y son tarifas que se cobran.
  const catalogResolved =
    mode === "country"
      ? countryCatalog != null
      : mode === "coverage" &&
        regionOptions.length > 0 &&
        regionOptions.every((r) => communeStatus[r.value] === "loaded");

  const isOutOfCatalog = useCallback(
    (communeName) =>
      catalogResolved &&
      !!String(communeName ?? "").trim() &&
      !selectableNames.has(normalizeCommuneName(communeName)),
    [catalogResolved, selectableNames],
  );

  return {
    /** 'loading' | 'coverage' | 'country' */
    mode,
    /** false = la cobertura no se pudo leer (distinto de "no declaró cobertura"). */
    coverageKnown,
    countryName,
    regionOptions,
    communeOptions,
    ensureCommunes,
    communeStatus,
    loadingRegions,
    catalogResolved,
    isOutOfCatalog,
  };
}
